import { useShowToast } from '@/hooks';
import {
  useApprovalRemainderMutation,
  useApproveRejectActionMutation,
} from '@/store/query/approval-builder';
import { useEffect, useState, useMemo, useRef } from 'react';
import {
  Avatar,
  Box,
  Flex,
  Icon,
  Tooltip,
  useDisclosure,
  Circle,
  Spinner,
  Center,
} from '@chakra-ui/react';
import { KModal, KTag, KText } from '@/ui-library/components';
import { CenterSpinner, ModalWithForm } from '@/components/ats';
import { FormBuilder } from '@/components/formBuilder';
import { APPROVAL_NOTE_FORM_CONFIG } from './constants';
import { useSelector } from 'react-redux';
import { selectCurrUser } from '@/store/bootstrap/selectors';
import {
  Bell01,
  CheckCircle as checkCircleDual,
  MinusCircle,
  XCircle,
} from '@kula-ai/icons-core';

const ENTITY_LABELS = {
  offer: { singular: 'offer', capitalized: 'Offer' },
  requisition: { singular: 'requisition', capitalized: 'Requisition' },
  job: { singular: 'job', capitalized: 'Job' },
};

export const ApprovalFlowSteps = ({
  config = {},
  isLoading,
  onClose = () => {},
  offerId, // Keep for backward compatibility
  entityId, // NEW: Generic entity ID
  entityType = 'offer', // NEW: 'offer' | 'requisition' | 'job'
  onSuccess = () => {},
  handleModalOpen,
  handleUserSelect,
  renderModalInParent = false,
  viewMode = false, // NEW: When true, hides actions, send reminder, and pending tags
  applyToGroup = false, // NEW: When true, includes apply_to_group in approval payload
  renderAdditionalInfo = null, // NEW: Function that returns additional JSX to render below the title
  containerStyles = {},
}) => {
  const actualEntityId = entityId || offerId; // Use entityId if provided, fallback to offerId
  const entityLabel = ENTITY_LABELS[entityType] || ENTITY_LABELS.offer;
  const { approval_steps = [], id, current_user_permissions = {} } = config;
  const { can_approve_on_behalf = false } = current_user_permissions || {};

  const toast = useShowToast();
  const [sendRemainder, { isLoading: isSendRemainderLoading }] =
    useApprovalRemainderMutation();
  const [stepStatus, setStepStatus] = useState([]);
  const [approverAcceptAction, { isLoading: isApproverActionLoading }] =
    useApproveRejectActionMutation();
  const approverActions = useDisclosure();
  const [selectedUser, setSelectedUser] = useState(null);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const currUser = useSelector(selectCurrUser);
  const [currReminder, setCurrReminder] = useState(null);
  const approvalNoteFormRef = useRef(null);
  const approvingUserIdRef = useRef(null);

  // Create a stable key from approval_steps to avoid unnecessary re-renders
  const approvalStepsKey = useMemo(() => {
    return approval_steps.map((step) => `${step.id}-${step.status}`).join(',');
  }, [approval_steps]);

  useEffect(() => {
    const initialStatus = approval_steps.map((step) => step.status);
    setStepStatus(initialStatus);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [approvalStepsKey]);

  const reminderText = 'Send reminder';

  const handleCurrentUserApprove = async () => {
    if (approvingUserIdRef.current) return;
    approvingUserIdRef.current = currUser?.id;
    approverAcceptAction({
      payload: {
        type: entityType,
        entity_id: actualEntityId,
        log_action: 'approve',
        note: '',
        ...(applyToGroup ? { apply_to_group: true } : {}),
      },
    })
      .then((response) => {
        approvingUserIdRef.current = null;
        if ('error' in response) {
          const message = response?.error?.[0];
          toast({
            title: `Unable to approve the ${entityLabel.singular}`,
            description: message,
            status: 'error',
          });
        } else {
          toast({
            title: `${entityLabel.capitalized} approved successfully`,
          });
          onClose();
          onSuccess();
        }
      })
      .catch(() => {
        approvingUserIdRef.current = null;
      });
  };

  const handleCurrentUserReject = async (values = {}) => {
    approverAcceptAction({
      payload: {
        type: entityType,
        entity_id: actualEntityId,
        log_action: 'reject',
        note: values.note || '--',
        ...(applyToGroup ? { apply_to_group: true } : {}),
      },
    }).then((response) => {
      if ('error' in response) {
        const message = response?.error?.[0];
        toast({
          title: `Unable to reject the ${entityLabel.singular}`,
          description: message,
          status: 'error',
        });
      } else {
        toast({
          title: `${entityLabel.capitalized} has been rejected`,
        });
        setShowRejectModal(false);
        onClose();
        onSuccess();
      }
    });
  };

  const handleApproveOnBehalf = async (userId) => {
    if (approvingUserIdRef.current) return;
    approvingUserIdRef.current = userId;

    const note = approvalNoteFormRef.current?.values?.note;
    approverAcceptAction({
      payload: {
        type: entityType,
        entity_id: actualEntityId,
        log_action: 'approve',
        user_id: userId,
        ...(note && { note }),
        ...(applyToGroup ? { apply_to_group: true } : {}),
      },
    })
      .then((response) => {
        approvingUserIdRef.current = null;
        if ('error' in response) {
          const message = response?.error?.[0];
          toast({
            title: `Unable to approve the ${entityLabel.singular}`,
            description: message,
            status: 'error',
          });
        } else {
          toast({
            title: `${entityLabel.capitalized} approved successfully`,
          });
          approverActions?.onClose();
          onClose();
          onSuccess();
        }
      })
      .catch(() => {
        approvingUserIdRef.current = null;
      });
  };

  if (isLoading) return <CenterSpinner h="100px" />;

  const renderUser = (
    user = {},
    stepIndex,
    approvalId,
    isLastUserInGroup = false
  ) => {
    const { approval_log = {}, id, user: userData = {} } = user;
    const { name, avatar, imageUrl, id: userId } = userData;
    const imageUrlResolved = imageUrl ?? avatar;
    const { submitted_by_user = {} } = approval_log || {};
    const isApproved = approval_log?.action === 'approve';
    const isRejected = approval_log?.action === 'reject';
    const isStepApproved = stepStatus[stepIndex] === 'approved';
    const isStepRejected = stepStatus[stepIndex] === 'rejected';
    const isPreviousStepApproved =
      stepIndex === 0 || stepStatus[stepIndex - 1] === 'approved';
    const showRemainder =
      !isStepApproved &&
      isPreviousStepApproved &&
      !isStepRejected &&
      !approval_log;
    const isCurrUserApproval = currUser?.id === userId;
    const showApproveAndReject =
      isCurrUserApproval && !isStepApproved && !isStepRejected && !approval_log;

    const isCurrentUser = currUser?.id === userId;
    const showApproveAndReminder = can_approve_on_behalf && !isCurrentUser;
    const showApproveOnBehalf =
      can_approve_on_behalf && submitted_by_user?.id !== userId;

    const isApproving =
      isApproverActionLoading && approvingUserIdRef.current === userId;

    const icon = isApproved ? checkCircleDual : isRejected ? XCircle : null;
    const iconColor = isApproved
      ? 'pastel-2-dark'
      : isRejected
        ? 'pastel-1-dark'
        : 'border-color';
    const iconBg = isApproved
      ? 'pastel-2-light'
      : isRejected
        ? 'pastel-1-light'
        : 'white';

    const showNextStepTooltip =
      !isApproved && !isRejected && !isPreviousStepApproved;

    return (
      <Flex
        key={id}
        flexDir="column"
        gap="6px"
        mt="5px"
        position="relative"
        cursor="default"
        onClick={(event) => {
          event?.stopPropagation();
        }}
      >
        {!isLastUserInGroup && (
          <Box
            position="absolute"
            left="10px"
            top="22px"
            bottom={-6}
            w="1px"
            bg="border-color"
            zIndex={0}
            mt="3px"
          />
        )}
        <Tooltip
          variant="default"
          label={
            showNextStepTooltip &&
            `${name} will get the approval request after the previous step's approvals are complete`
          }
        >
          <Flex gap="6px" alignItems="center">
            <Flex alignItems="center" justifyContent="start" gap="6px">
              {icon ? (
                <Icon
                  as={icon}
                  color={'primary-500'}
                  h="24px"
                  w="24px"
                  sx={{
                    path: {
                      strokeWidth: '1.2px !important',
                    },
                  }}
                />
              ) : (
                <Circle
                  size="20px"
                  borderWidth="1px"
                  borderColor="border-color"
                  bg="white"
                />
              )}
              <Flex gap={1}>
                <Avatar
                  src={imageUrlResolved}
                  name={name}
                  size="2xs"
                  borderRadius="6px"
                  mt="2px"
                />
                <KText
                  isTruncated
                  enableTooltip={!showNextStepTooltip}
                  maxW={
                    isApproved || isRejected || showRemainder
                      ? '200px'
                      : '280px'
                  }
                >
                  {name}
                </KText>
              </Flex>
            </Flex>
            {!approval_log && showRemainder && !viewMode && (
              <KTag
                value="Pending"
                bg="white"
                color="secondary-700"
                border="1px solid"
                borderColor="border-color"
              />
            )}
            {(isApproved || isRejected) && (
              <KText
                size="sm-medium"
                bgColor={iconBg}
                color={iconColor}
                borderRadius="sm"
                p="4px 8px"
              >
                {isApproved ? 'Approved' : 'Rejected'}
              </KText>
            )}
          </Flex>
        </Tooltip>

        {showApproveOnBehalf && isApproved && (
          <Flex w="max-content" gap="4px" ml="30px" color="secondary-600">
            <KText size="sm-regular" color="secondary-600">
              on behalf by{' '}
            </KText>
            <KText
              isTruncated
              enableTooltip={true}
              maxW="200px"
              size="sm-regular"
              color="secondary-600"
            >
              {submitted_by_user?.name}
            </KText>
          </Flex>
        )}

        {approval_log?.note && (
          <Flex ml="23px" flexDir="column" alignItems="start" w="fit-content">
            <KText size="sm-bold" color="secondary-900">
              Reason
            </KText>
            <Tooltip
              label={approval_log?.note}
              hasArrow
              maxW="600px"
              isDisabled={approval_log?.note?.length < 150}
            >
              <Box maxW="400px">
                <KText
                  size="sm-medium"
                  wordBreak="break-all"
                  noOfLines={3}
                  cursor={
                    approval_log?.note?.length >= 150 ? 'pointer' : 'default'
                  }
                >
                  {approval_log?.note}
                </KText>
              </Box>
            </Tooltip>
          </Flex>
        )}
        <Flex ml="23px" gap="6px">
          {/* Current user approve/reject actions */}
          {showApproveAndReject &&
            isPreviousStepApproved &&
            !renderModalInParent &&
            !viewMode && (
              <>
                <Flex
                  alignItems="center"
                  gap="6px"
                  cursor={isApproving ? 'not-allowed' : 'pointer'}
                  onClick={(event) => {
                    event?.stopPropagation();
                    handleCurrentUserApprove();
                  }}
                  opacity={isApproving ? 0.6 : 1}
                >
                  {isApproving ? (
                    <Center minW="20px" minH="20px">
                      <Spinner
                        size="sm"
                        color="primary-500"
                        thickness="2px"
                        borderWidth="1px !important"
                      />
                    </Center>
                  ) : (
                    <Icon as={checkCircleDual} color="primary-500" />
                  )}
                  <KText
                    size="sm-medium"
                    color="primary-500"
                    data-test-id="approve-button"
                  >
                    {isApproving ? 'Approving...' : 'Approve'}
                  </KText>
                </Flex>
                <Flex
                  alignItems="center"
                  gap="6px"
                  cursor="pointer"
                  onClick={(event) => {
                    event?.stopPropagation();
                    setShowRejectModal(true);
                  }}
                >
                  <MinusCircle
                    size="1em"
                    color="var(--kula-colors-primary-500)"
                    className="kula-icon"
                  />
                  <KText
                    size="sm-medium"
                    color="primary-500"
                    data-test-id="reject-button"
                  >
                    Reject
                  </KText>
                </Flex>
              </>
            )}

          {/* Send reminder for other users */}
          {showRemainder && !isCurrentUser && !viewMode && (
            <Flex
              alignItems="center"
              gap="6px"
              cursor="pointer"
              onClick={async (event) => {
                if (isSendRemainderLoading && currReminder === id) return;
                setCurrReminder(id);
                event?.stopPropagation();

                const response = await sendRemainder({
                  approvalUserId: id,
                  approvalId,
                });

                if ('error' in response) {
                  toast({ title: 'Sending reminder failed', status: 'error' });
                } else {
                  toast({
                    title: 'Reminder sent successfully',
                    status: 'success',
                  });
                }
                // onClose(); // Skipping close as, they want to remind more users - changed on Requisition flow
              }}
            >
              {isSendRemainderLoading && currReminder === id ? (
                <Center w="120px">
                  <Spinner size="xs" color="primary-500" />
                </Center>
              ) : (
                <Flex alignItems="center" gap="6px">
                  <Bell01
                    size="1em"
                    color="var(--kula-colors-primary-500)"
                    className="kula-icon"
                  />
                  <KText
                    size="sm-medium"
                    color="primary-500"
                    data-test-id="send-reminder-button"
                  >
                    {reminderText}
                  </KText>
                </Flex>
              )}
            </Flex>
          )}

          {/* Approve on behalf for admins - skip if renderModalInParent is true */}
          {showRemainder && showApproveAndReminder && !viewMode && (
            <Flex
              alignItems="center"
              gap="6px"
              cursor={isApproving ? 'not-allowed' : 'pointer'}
              onClick={(event) => {
                event?.stopPropagation();
                if (renderModalInParent) {
                  handleUserSelect(user);
                  handleModalOpen();
                } else {
                  setSelectedUser(user);
                  approverActions?.onOpen();
                }
              }}
              opacity={isApproving ? 0.6 : 1}
            >
              {isApproving ? (
                <Center minW="20px" minH="20px">
                  <Spinner
                    size="sm"
                    color="primary-500"
                    thickness="2px"
                    borderWidth="1px !important"
                  />
                </Center>
              ) : (
                <Icon as={checkCircleDual} color="primary-500" />
              )}
              <KText
                size="sm-medium"
                color="primary-500"
                data-test-id="approve-offer-button"
              >
                {isApproving ? 'Approving...' : 'Approve'}
              </KText>
            </Flex>
          )}
        </Flex>
      </Flex>
    );
  };

  const renderStep = (step = {}, stepIndex, approvalId) => {
    const { id, approval_count, approval_user_groups = [], status } = step;

    const label =
      approval_count === 0 ? 'ALL NEED TO APPROVE' : 'ANYONE CAN APPROVE';

    const isApproved = status === 'approved';
    const isRejected = status === 'rejected';

    const icon = isApproved ? checkCircleDual : isRejected ? XCircle : null;
    const isLastStep = stepIndex === approval_steps.length - 1;

    return (
      <Flex key={id} flexDir="column" position="relative">
        <Box
          w="1px"
          h={isLastStep ? 'calc(100% - 20px)' : 'calc(100% - 10px)'}
          bg="border-color"
          position="absolute"
          top="22px"
          left="10px"
          zIndex={0}
        />
        <Flex gap="6px" alignItems="center" p="6px 0" zIndex={1}>
          {icon ? (
            <Icon
              as={icon}
              color={'primary-500'}
              w="24px"
              h="24px"
              sx={{
                path: {
                  strokeWidth: '1.2px !important',
                },
              }}
            />
          ) : (
            <Circle
              size="20px"
              borderWidth="1px"
              borderColor="border-color"
              bg="white"
            />
          )}
          <KText size="xs-semi-bold" color="secondary-600">
            {label}
          </KText>
        </Flex>
        <Flex flexDir="column" ml="30px" gap="6px">
          {approval_user_groups.map((group) => {
            const users = group?.approval_users || [];
            return users.map((user, idx) =>
              renderUser(user, stepIndex, approvalId, idx === users.length - 1)
            );
          })}
        </Flex>
      </Flex>
    );
  };

  return (
    <>
      <Flex
        flexDir="column"
        gap="10px"
        p="12px"
        maxH="400px"
        overflowY="auto"
        zIndex={99}
        {...containerStyles}
      >
        {!viewMode && (
          <KText size="md-medium" color="secondary-900" w="fit-content">
            Approval flow
          </KText>
        )}
        {renderAdditionalInfo && renderAdditionalInfo()}

        <Flex flexDir="column">
          {approval_steps.map((step, stepIndex) =>
            renderStep(step, stepIndex, id)
          )}
        </Flex>
      </Flex>
      {/* Approve on behalf modal - only show when renderModalInParent is false */}
      {approverActions?.isOpen && !renderModalInParent && (
        <KModal
          isOpen={approverActions?.isOpen}
          onClose={approverActions?.onClose}
          title={`Approve on behalf of ${selectedUser?.user?.name || ''}`}
          ctaLabel="Approve"
          loadingText="Approving"
          ctaLoading={isApproverActionLoading}
          ctaAction={(event) => {
            event?.stopPropagation();
            if (selectedUser?.user?.id && !approvingUserIdRef.current) {
              handleApproveOnBehalf(selectedUser.user.id);
            }
          }}
          size="md"
          closeOnOverlayClick={false}
          modalHeaderProps={{
            p: '22px 24px 8px 24px',
          }}
          modalOverlayProps={{
            zIndex: 1500,
            className: 'modal-container-z-index-1500',
          }}
        >
          <Flex flexDir="column" gap="10px">
            <KText>
              Approving for {selectedUser?.user?.name} will move the approval
              flow forward as though they did it.
            </KText>
            <KText>This is permanent and cannot be reversed.</KText>
            <Box mt="-12px">
              <FormBuilder
                config={APPROVAL_NOTE_FORM_CONFIG}
                formRef={approvalNoteFormRef}
              />
            </Box>
          </Flex>
        </KModal>
      )}
      {showRejectModal && (
        <ModalWithForm
          isOpen={showRejectModal}
          onClose={() => setShowRejectModal(false)}
          headerText={'Reason for rejection?'}
          subHeaderText={`Provide detailed feedback on why you are rejecting this ${entityLabel.singular}`}
          primaryCtaText="Reject"
          modalOverlayProps={{
            zIndex: 1500,
            className: 'modal-container-z-index-1500',
          }}
          formConfig={[
            {
              name: 'note',
              type: 'textArea',
              label: 'Notes',
              placeholder: 'Write your reason here',
            },
          ]}
          handleSubmit={handleCurrentUserReject}
          isCtaLoading={isApproverActionLoading}
          autoFocus={false}
          formContainerStyles={{
            m: '-10px 0',
          }}
          skipDirtyCheck={true}
        />
      )}
    </>
  );
};
